import { type EmojiCode, type EmojiDef, emojisByCode } from './emojis';
import { emojiUnicode } from './emojiToUnicode';

/**
 * Returns the UTF-8 string of an emoji character for the given emoji code
 */
export function getEmojiForCode(input: EmojiCode): string {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return '';
    }

    const emoji = emojisByCode[code];
    if (!emoji) {
        return '';
    }

    const unicode = emoji.utf8 ?? code;
    const codePoints = unicode.split('-').map((elt) => Number.parseInt(elt, 16));
    return String.fromCodePoint(...codePoints);
}

/**
 * Extracts the Emoji definition from text, or returns null if it's not a valid emoji.
 */
export function extractEmojiFromText(str: string): EmojiDef | null {
    if (!str) {
        return null;
    }

    const unicode = emojiUnicode(str, '-');
    const code = getEmojiCodeForSequence(unicode);
    if (!code) {
        return null;
    }

    return emojisByCode[code] ?? null;
}

/**
 * Extract the emoji from a title.
 */
export function extractEmojiFromTitle(text: string): { title: string; emoji?: string } {
    const parts = text.trim().split(' ');
    const emoji = parts[0] ? extractEmojiFromText(parts[0]) : null;

    if (emoji) {
        return {
            title: parts.slice(1).join(' '),
            emoji: emoji.code,
        };
    }

    return {
        title: parts.join(' '),
    };
}

/**
 * Normalize an emoji code.
 * The input can be an emoji code, or an emoji character.
 * It returns null if the code is not valid.
 */
export function getEmojiCode(input: string): string | null {
    const code = normalizeEmojiCode(input);

    if (!code) {
        return null;
    }

    if (emojisByCode[code]) {
        return code;
    }

    const emoji = extractEmojiFromText(code);
    if (emoji) {
        return emoji.code;
    }

    return null;
}

let reverseEmojiSequences: null | { [sequence: string]: EmojiCode } = null;

/**
 * Get the full UTF-8 sequence for an emoji code.
 */
export function getEmojiCodeForSequence(unicode: string): EmojiCode | null {
    if (emojisByCode[unicode]) {
        // Basic emoji code
        return unicode;
    }

    if (!reverseEmojiSequences) {
        // Lazy initialization of the reverse index for complex utf8 sequences
        const index: Record<string, string> = {};
        Object.entries(emojisByCode).forEach(([code, emoji]) => {
            if (emoji.utf8) {
                index[emoji.utf8] = code;
            }
        });
        reverseEmojiSequences = index;
    }

    return reverseEmojiSequences[unicode] ?? null;
}

function normalizeEmojiCode(code: string): string {
    return code.toLowerCase();
}
